/**
 * Board/epic context for AI scheduling — frontend loads data;
 * model proposes DUE, then we clamp (never past; default today).
 */

export type ScheduleHorizon = 'short' | 'near' | 'long';

export type ScheduleBoardTask = {
  id: string;
  title: string | null;
  strategy_score: number | null;
  due_at: string;
  epic_id: string | null;
};

export type ScheduleBoardContext = {
  todayIso: string; // YYYY-MM-DD
  epicTitle?: string | null;
  epicTargetDate?: string | null; // YYYY-MM-DD or ISO date
  /** Open tasks already on the timeline (occupancy + load). */
  openTasks: ScheduleBoardTask[];
};

export function toDateOnly(isoOrDate: string | null | undefined): string | null {
  if (!isoOrDate) return null;
  const m = String(isoOrDate).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseScheduleHorizon(raw: string): ScheduleHorizon | null {
  const t = raw.trim().toLowerCase();
  if (/^short\b|short[-\s]?term|urgent|asap|today|tomorrow/.test(t)) return 'short';
  if (/^near\b|near[-\s]?term|this week|next week|soon/.test(t)) return 'near';
  if (/^long\b|long[-\s]?term|later|month|eventually/.test(t)) return 'long';
  return null;
}

function localTodayParts(todayIso: string): { y: number; m: number; d: number } {
  const [y, m, d] = todayIso.split('-').map(Number);
  return { y, m, d };
}

/** Local calendar datetime string YYYY-MM-DDTHH:mm (no UTC shift). */
export function localDateAtHour(dayIso: string, hour = 9, minute = 0): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const [y, m, d] = dayIso.split('-').map(Number);
  return `${y}-${pad(m)}-${pad(d)}T${pad(hour)}:${pad(minute)}`;
}

export function addDaysIso(dayIso: string, delta: number): string {
  const { y, m, d } = localTodayParts(dayIso);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function occupiedDays(ctx: ScheduleBoardContext): Set<string> {
  const set = new Set<string>();
  for (const t of ctx.openTasks) {
    const due = toDateOnly(t.due_at);
    if (due) set.add(due);
  }
  return set;
}

/**
 * Never schedule before today. Past / invalid → today.
 */
export function clampDueNotPast(
  dueLocal: string | null | undefined,
  todayIso: string,
): string {
  const day = toDateOnly(dueLocal ?? null);
  if (!day || day < todayIso) return localDateAtHour(todayIso, 9, 0);
  // Preserve time-of-day if present and day is valid
  if (dueLocal && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dueLocal)) {
    return dueLocal.slice(0, 16);
  }
  return localDateAtHour(day, 9, 0);
}

/**
 * Next calendar day on/after `fromIso` with no open task (includes today).
 * Caps at epic target when that target is still in the future.
 */
export function nextOpenDay(
  ctx: ScheduleBoardContext,
  fromIso: string,
  maxScan = 45,
): string {
  const occupied = occupiedDays(ctx);
  let epicCap = ctx.epicTargetDate ? toDateOnly(ctx.epicTargetDate) : null;
  if (epicCap && epicCap < ctx.todayIso) epicCap = null; // past deadline — ignore

  let cursor = fromIso < ctx.todayIso ? ctx.todayIso : fromIso;
  for (let i = 0; i < maxScan; i++) {
    if (epicCap && cursor > epicCap) {
      return epicCap >= ctx.todayIso ? epicCap : ctx.todayIso;
    }
    if (!occupied.has(cursor)) return cursor;
    cursor = addDaysIso(cursor, 1);
  }
  return ctx.todayIso;
}

/** Plain-text block for the model — crowded days + epic window. */
export function formatScheduleBoardContext(ctx: ScheduleBoardContext): string {
  const epicDay = ctx.epicTargetDate ? toDateOnly(ctx.epicTargetDate) : null;
  const epicPast = Boolean(epicDay && epicDay < ctx.todayIso);

  const lines: string[] = [
    `Today: ${ctx.todayIso}`,
    ctx.epicTitle ? `Epic: ${ctx.epicTitle}` : 'Epic: (none / ops)',
    epicPast
      ? `Epic target_date: ${epicDay} — PAST. Do NOT use it. Schedule from today forward only.`
      : epicDay
        ? `Epic target_date: ${epicDay} — keep DUE between today and that date when possible.`
        : 'Epic target_date: none — use horizon bands (short = today, near 3–10d, long 11–30d).',
    '',
    'DEFAULT: DUE = today unless horizon/sequence clearly places this task later.',
    'NEVER schedule before today. NEVER use a past epic deadline as the due date.',
    '',
    'Open tasks already on the timeline (prefer an empty day when pushing later):',
  ];

  if (ctx.openTasks.length === 0) {
    lines.push('- (none)');
  } else {
    for (const t of ctx.openTasks.slice(0, 32)) {
      const due = toDateOnly(t.due_at) || t.due_at.slice(0, 10);
      const title = (t.title || 'Untitled').slice(0, 48);
      const sc = t.strategy_score != null ? `${t.strategy_score}/5` : '—';
      lines.push(`- ${due} · ${sc} · ${title}`);
    }
  }

  const load = new Map<string, number>();
  for (const t of ctx.openTasks) {
    const due = toDateOnly(t.due_at);
    if (!due || due < ctx.todayIso) continue;
    load.set(due, (load.get(due) || 0) + 1);
  }
  if (load.size > 0) {
    lines.push('', 'Task load by day (from today):');
    for (const [day, n] of [...load.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
      lines.push(`- ${day}: ${n} task${n === 1 ? '' : 's'}`);
    }
  }

  return lines.join('\n').slice(0, 3500);
}

/**
 * Resolve create due: clamp AI pick; default today; sequence later only when horizon says so.
 */
export function resolveCreateDue(
  ctx: ScheduleBoardContext,
  preferredDueLocal: string | null | undefined,
  horizon: ScheduleHorizon | null,
): string {
  const preferredDay = toDateOnly(preferredDueLocal ?? null);

  // Valid AI/future pick on or after today → keep (clamped)
  if (preferredDay && preferredDay >= ctx.todayIso) {
    return clampDueNotPast(preferredDueLocal, ctx.todayIso);
  }

  // No usable preferred (missing or past) — place by horizon
  const h = horizon ?? 'short';
  if (h === 'short' || !horizon) {
    return localDateAtHour(ctx.todayIso, 9, 0);
  }

  // near / long: first open day in the band (never past)
  const bandStart = h === 'near' ? 3 : 11;
  const candidate = nextOpenDay(ctx, addDaysIso(ctx.todayIso, bandStart));
  return localDateAtHour(candidate, 9, 0);
}

/**
 * Soft fallback ONLY if the API fails — default today; never past.
 * @deprecated Prefer resolveCreateDue
 */
export function softScheduleFallback(
  ctx: ScheduleBoardContext,
  horizon: ScheduleHorizon | null,
): string {
  return resolveCreateDue(ctx, null, horizon);
}
